import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import './AdminMcp.css';

/**
 * MCP-fliken i admin-panelen — owner-only.
 * Visar:
 *  - anslutningsstatus mot cs2-mcp-public
 *  - lista av tools som MCP-servern exponerar
 *  - in-memory telemetry (count, error_rate, avg/max latency per tool)
 */
export default function AdminMcp({ hideTitle = false } = {}) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState(null);
  const [telemetry, setTelemetry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statusRes, telemetryRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/mcp/status`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/mcp/telemetry`, { headers: getAuthHeaders() }),
      ]);
      if (statusRes.ok) setStatus(await statusRes.json());
      else setStatus({ connected: false, error: `HTTP ${statusRes.status}` });
      if (telemetryRes.ok) setTelemetry(await telemetryRes.json());
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="admin-mcp">
      <div className="admin-mcp-header">
        {!hideTitle && <h2 className="section-heading">{t('admin.mcpServer')}</h2>}
        <button className="admin-mcp-refresh" onClick={refresh} disabled={loading}>
          {loading ? t('live.loading') : t('admin.refresh')}
        </button>
      </div>

      {error && <div className="admin-mcp-error">⚠ {error}</div>}

      <section className="admin-mcp-section">
        <h3 className="admin-mcp-subtitle">{t('admin.mcpConnection')}</h3>
        {!status && <div className="admin-mcp-loading">…</div>}
        {status && status.connected && (
          <div className="admin-mcp-status admin-mcp-status-ok">
            ✓ {t('admin.mcpConnected', { count: status.tool_count })}
          </div>
        )}
        {status && !status.connected && (
          <div className="admin-mcp-status admin-mcp-status-bad">
            ✕ {t('admin.mcpNotConnected', { error: status.error || t('notify.unknownError') })}
            <div className="admin-mcp-hint">
              {t('admin.mcpHintPre')} <code>MCP_PUBLIC_URL</code> {t('admin.mcpHintMid')}
              <code> .env</code> {t('admin.mcpHintPost')}
            </div>
          </div>
        )}
      </section>

      {status?.tools?.length > 0 && (
        <section className="admin-mcp-section">
          <h3 className="admin-mcp-subtitle">{t('admin.mcpExposedTools')}</h3>
          <div className="admin-mcp-tools-grid">
            {status.tools.map((tool) => (
              <div key={tool.name} className="admin-mcp-tool-card">
                <div className="admin-mcp-tool-name">{tool.name}</div>
                <div className="admin-mcp-tool-desc">{tool.description}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="admin-mcp-section">
        <h3 className="admin-mcp-subtitle">{t('admin.mcpUsage')}</h3>
        {!telemetry && <div className="admin-mcp-loading">…</div>}
        {telemetry && telemetry.tool_count === 0 && (
          <div className="admin-mcp-empty">
            {t('admin.mcpNoData')}
          </div>
        )}
        {telemetry && telemetry.tool_count > 0 && (
          <>
            <div className="admin-mcp-totals">
              <span><strong>{telemetry.total_calls}</strong> {t('admin.mcpCallsTotal')}</span>
              <span className="admin-mcp-totals-sep">·</span>
              <span><strong>{telemetry.total_errors}</strong> {t('admin.mcpErrorsWord')}</span>
              <span className="admin-mcp-totals-sep">·</span>
              <span>{t('admin.mcpToolsUsed', { count: telemetry.tool_count })}</span>
            </div>
            <table className="admin-mcp-telemetry-table">
              <thead>
                <tr>
                  <th>{t('admin.colTool')}</th>
                  <th className="num">Calls</th>
                  <th className="num">{t('admin.colErrors')}</th>
                  <th className="num">{t('admin.colErrorPct')}</th>
                  <th className="num">Avg ms</th>
                  <th className="num">Max ms</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(telemetry.aggregates).map(([key, agg]) => (
                  <tr key={key}>
                    <td className="admin-mcp-tool-key">{key}</td>
                    <td className="num">{agg.count}</td>
                    <td className={`num ${agg.errors > 0 ? 'admin-mcp-bad' : ''}`}>{agg.errors}</td>
                    <td className="num">{(agg.error_rate * 100).toFixed(1)}%</td>
                    <td className="num">{agg.avg_latency_ms?.toFixed(1)}</td>
                    <td className="num">{agg.max_latency_ms?.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>
    </div>
  );
}
